import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState, useRef, useEffect } from "react";
import SScaleLoader from "../SScaleLoader";
import { SCardBody } from "../SCard";
import STooltip from "../Tooltip";
import { SButton, SIcon, SIconType } from "../components";
export const STable = ({ columns, data: _data, isLoading, error, highlightRow, onRowClick, onColumnClick, onColumnHover, additionalCellClassName, hoverClassName = "hover:bg-grayscale-light", tableClassName = "", tableContainerClassName = "", firstColumnClasses = "", sort, limit, showsBottomBorderWithLimit = true, renderRow = (children)=>children, rowClassName = "", showSortingDirection = false, loadingClassName = "" })=>{
    const tableRef = useRef(null);
    const [showAll, setShowAll] = useState(false);
    const data = useMemo(()=>{
        return limit && !showAll ? _data?.slice(0, limit) : _data;
    }, [
        _data,
        limit,
        showAll
    ]);
    useEffect(()=>{
        if (!tableRef.current) return;
        const table = tableRef.current;
        const headerCells = table.querySelectorAll('div[style*="display: table-cell"]');
        const rows = table.querySelectorAll('div[style*="display: table-row-group"] > a');
        let leftOffset = 0;
        columns.forEach((column, index)=>{
            if (column.fixed) {
                const headerCell = headerCells[index];
                headerCell.classList.add("sticky", "z-10", "bg-grayscale-dark", "whitespace-nowrap");
                headerCell.style.left = `${leftOffset}px`;
                rows.forEach((row)=>{
                    const cell = row.children[index];
                    cell.classList.add("sticky", "z-10", "bg-grayscale-dark", "whitespace-nowrap");
                    cell.style.left = `${leftOffset}px`;
                });
                leftOffset += headerCell.offsetWidth;
            }
        });
        const updateFixedColumnsShadow = ()=>{
            const scrollLeft = table.scrollLeft;
            const fixedColumns = columns.filter((col)=>col.fixed);
            const shadowClass = "shadow-[10px_4px_10px_0px_rgba(0,0,0,0.2)]";
            fixedColumns.forEach((col)=>{
                const colIndex = columns.findIndex((c)=>c === col);
                const headerCell = headerCells[colIndex];
                const leftPosition = parseInt(headerCell.style.left);
                if (scrollLeft >= 5) {
                    headerCell.classList.add(...[
                        shadowClass,
                        "transition-shadow"
                    ]);
                    rows.forEach((row)=>{
                        ;
                        row.children[colIndex].classList.add(...[
                            shadowClass,
                            "transition-shadow"
                        ]);
                    });
                } else {
                    headerCell.classList.remove(...[
                        shadowClass
                    ]);
                    rows.forEach((row)=>{
                        ;
                        row.children[colIndex].classList.remove(...[
                            shadowClass
                        ]);
                    });
                }
            });
        };
        updateFixedColumnsShadow();
        table.addEventListener("scroll", updateFixedColumnsShadow);
        return ()=>table.removeEventListener("scroll", updateFixedColumnsShadow);
    }, [
        columns,
        data
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: `pl-6 opacity-50 pt-10 py-5 flex flex-row items-center justify-center ${loadingClassName}`,
            children: /*#__PURE__*/ _jsx(SScaleLoader, {})
        });
    }
    if (error) {
        console.error("[!] STable error", error);
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsx(SCardBody, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "",
                    children: [
                        "😅 ",
                        /*#__PURE__*/ _jsx("span", {
                            className: "opacity-50",
                            children: "Something went wrong"
                        })
                    ]
                })
            })
        });
    }
    if ((data || []).length === 0) {
        return /*#__PURE__*/ _jsx(SCardBody, {
            children: /*#__PURE__*/ _jsx("p", {
                className: "opacity-50",
                children: "No data yet."
            })
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: `overflow-x-auto ${tableContainerClassName}`,
                style: {
                    scrollbarColor: "rgba(255,255,255,0.1) transparent",
                    scrollbarWidth: "auto",
                    scrollbarGutter: "auto",
                    // MS equivalent
                    msScrollbarBaseColor: "transparent",
                    msScrollbarTrackColor: "rgba(255,255,255,0.1)",
                    msScrollbarDarkshadowColor: "transparent",
                    msScrollbarHighlightColor: "transparent",
                    msScrollbarShadowColor: "transparent"
                },
                ref: tableRef,
                children: /*#__PURE__*/ _jsxs("div", {
                    style: {
                        display: "table"
                    },
                    className: `table-auto text-left w-full ${tableClassName}`,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            style: {
                                display: "table-header-group"
                            },
                            children: /*#__PURE__*/ _jsx("div", {
                                style: {
                                    display: "table-row"
                                },
                                className: "text-sm whitespace-nowrap",
                                children: columns.map((column, i)=>/*#__PURE__*/ _jsx("div", {
                                        style: {
                                            display: "table-cell",
                                            position: column.fixed ? "sticky" : "static",
                                            left: column.fixed ? 0 : "auto",
                                            zIndex: column.fixed ? 10 : "auto"
                                        },
                                        onClick: (event)=>onColumnClick && onColumnClick(column, event),
                                        onMouseEnter: ()=>onColumnHover && onColumnHover(column),
                                        onMouseLeave: ()=>onColumnHover && onColumnHover(null),
                                        className: `p-4 ${i == 0 ? firstColumnClasses : ""} ${column.key == sort?.key ? "text-brand-primary" : "text-white/50 font-normal"} ${column.headerClasses || ""} ${column.fixed ? "bg-grayscale-dark" : ""}`,
                                        children: column.headerContent ? column.headerContent() : column.headerTooltip === undefined ? /*#__PURE__*/ _jsxs("div", {
                                            className: ` ${onColumnClick ? `!cursor-pointer active:scale-[0.95] transition-all hover:text-white/100` : ""} ${showSortingDirection ? "flex items-center  gap-2 active:scale-100" : ""} ${showSortingDirection && column.key == sort?.key ? "!text-brand-primary" : ""} ${column.fixed ? "justify-start" : column.headerAlign ? `justify-${column.headerAlign}` : "justify-end"}`,
                                            children: [
                                                column.title,
                                                showSortingDirection && column.key == sort?.key && /*#__PURE__*/ _jsx("div", {
                                                    className: `transition-transform ${column.key == sort?.key ? "opacity-100" : "opacity-0"} ${sort?.direction == "asc" ? "scale-y-[-1]" : ""}`,
                                                    children: /*#__PURE__*/ _jsx(SIcon, {
                                                        size: 14,
                                                        type: "chevron-down"
                                                    })
                                                })
                                            ]
                                        }) : /*#__PURE__*/ _jsx(STooltip, {
                                            content: /*#__PURE__*/ _jsx("div", {
                                                className: `"!max-w-[468px]" `,
                                                children: column.headerTooltip
                                            }),
                                            placement: "top",
                                            children: /*#__PURE__*/ _jsxs("span", {
                                                className: "select-none inline-flex items-center gap-2",
                                                children: [
                                                    column.title,
                                                    column.key == sort?.key ? /*#__PURE__*/ _jsx("div", {
                                                        className: `transition-transform ${sort?.direction == "asc" ? "scale-y-[-1]" : ""}`,
                                                        children: /*#__PURE__*/ _jsx(SIcon, {
                                                            size: 14,
                                                            type: "chevron-down"
                                                        })
                                                    }) : column.tooltipIcon === undefined ? /*#__PURE__*/ _jsx(SIcon, {
                                                        type: "info-filled",
                                                        size: 14
                                                    }) : column.tooltipIcon
                                                ]
                                            })
                                        })
                                    }, i))
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            style: {
                                display: "table-row-group",
                                width: "100%"
                            },
                            children: (data || []).map((row, i)=>renderRow(/*#__PURE__*/ _jsx("a", {
                                    target: "_blank",
                                    style: {
                                        display: "table-row"
                                    },
                                    className: `${onRowClick !== undefined ? highlightRow === undefined || highlightRow(row) ? `cursor-pointer ${hoverClassName} hover:bg-opacity-70` : "" : ""} ${rowClassName}`,
                                    onClick: (event)=>{
                                        if (onRowClick !== undefined) {
                                            onRowClick(row, i);
                                        }
                                    },
                                    children: columns.map((column, j)=>/*#__PURE__*/ _jsx("div", {
                                            style: {
                                                display: "table-cell",
                                                verticalAlign: "middle",
                                                position: column.fixed ? "sticky" : "static",
                                                left: column.fixed ? 0 : "auto",
                                                zIndex: column.fixed ? 10 : "auto"
                                            },
                                            className: `border-t ${data?.length == i + 1 ? "border-b" : ""} border-white/7 p-4 py-3 ${column.cellClasses || ""} ${additionalCellClassName ? additionalCellClassName(row, i, j) : ""} ${j == 0 ? firstColumnClasses : ""} ${column.fixed ? "bg-grayscale-dark" : ""}`,
                                            children: column.valueFormatter(row, i) === undefined ? /*#__PURE__*/ _jsx("span", {
                                                className: "opacity-20",
                                                children: "—"
                                            }) : column.cellTooltipFormatter === undefined ? /*#__PURE__*/ _jsx(_Fragment, {
                                                children: column.valueFormatter(row, i)
                                            }) : /*#__PURE__*/ _jsx(STooltip, {
                                                content: /*#__PURE__*/ _jsx("div", {
                                                    className: "!max-w-[500px]",
                                                    children: column.cellTooltipFormatter(row)
                                                }),
                                                placement: "top",
                                                children: /*#__PURE__*/ _jsx("span", {
                                                    className: "cursor-default",
                                                    children: column.valueFormatter(row, i)
                                                })
                                            })
                                        }, j))
                                }, i), row, i))
                        })
                    ]
                })
            }),
            _data && limit && _data.length > limit && /*#__PURE__*/ _jsx("div", {
                className: `px-2 py-2 ${showsBottomBorderWithLimit ? "border-b border-white/7" : ""} `,
                children: /*#__PURE__*/ _jsxs(SButton, {
                    isMinimal: true,
                    onClick: ()=>setShowAll(!showAll),
                    children: [
                        showAll ? "Show less" : `Show ${_data.length - limit} more`,
                        /*#__PURE__*/ _jsx(SIcon, {
                            className: showAll ? "rotate-180" : "",
                            size: 14,
                            type: SIconType.ChevronDown
                        })
                    ]
                })
            })
        ]
    });
};
export const STransposedTable = ({ columns, data, isLoading, error, onColumnClick, renderColumn = (children)=>children, columnClassName = "" })=>{
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "pl-6 opacity-50 pt-10 py-5 flex flex-row items-center justify-center ",
            children: /*#__PURE__*/ _jsx(SScaleLoader, {})
        });
    }
    if (error) {
        console.error("[!] STransposedTable error", error);
        return /*#__PURE__*/ _jsx(SCardBody, {
            children: /*#__PURE__*/ _jsx("p", {
                className: "opacity-50 pl-2",
                children: "😅 Something went wrong"
            })
        });
    }
    if ((data || []).length === 0) {
        return /*#__PURE__*/ _jsx(SCardBody, {
            children: /*#__PURE__*/ _jsx("p", {
                className: "opacity-50 pl-2",
                children: "No data yet."
            })
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "overflow-x-auto",
        children: /*#__PURE__*/ _jsx("div", {
            className: "table w-full",
            children: columns.map((column, columnIndex)=>renderColumn(/*#__PURE__*/ _jsxs("div", {
                    className: `table-row ${onColumnClick !== undefined ? "cursor-pointer hover:bg-grayscale-light hover:bg-opacity-70" : ""} ${columnClassName}`,
                    onClick: ()=>onColumnClick && onColumnClick(column, columnIndex),
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: `p-2 table-cell border-t border-white/7 opacity-50 font-normal whitespace-nowrap ${column.headerClasses || ""}`,
                            children: column.headerTooltip === undefined ? /*#__PURE__*/ _jsx(_Fragment, {
                                children: column.title
                            }) : /*#__PURE__*/ _jsx(STooltip, {
                                content: /*#__PURE__*/ _jsx("div", {
                                    className: "!max-w-[468px]",
                                    children: column.headerTooltip
                                }),
                                placement: "top",
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "cursor-default inline-flex items-center gap-2",
                                    children: [
                                        column.title,
                                        " ",
                                        /*#__PURE__*/ _jsx(SIcon, {
                                            type: "info-filled",
                                            size: 14
                                        })
                                    ]
                                })
                            })
                        }),
                        data && data.map((rowData, rowIndex)=>/*#__PURE__*/ _jsx("div", {
                                className: `p-2 table-cell border-t border-white/7 ${column.cellClasses || ""}`,
                                children: column.valueFormatter(rowData, rowIndex) === undefined ? /*#__PURE__*/ _jsx("span", {
                                    className: "opacity-20",
                                    children: "—"
                                }) : column.cellTooltipFormatter === undefined ? /*#__PURE__*/ _jsx(_Fragment, {
                                    children: column.valueFormatter(rowData, rowIndex)
                                }) : /*#__PURE__*/ _jsx(STooltip, {
                                    content: /*#__PURE__*/ _jsx("div", {
                                        className: "!max-w-[500px]",
                                        children: column.cellTooltipFormatter(rowData)
                                    }),
                                    placement: "top",
                                    children: /*#__PURE__*/ _jsx("span", {
                                        className: "cursor-default",
                                        children: column.valueFormatter(rowData, rowIndex)
                                    })
                                })
                            }, `${columnIndex}-${rowIndex}`))
                    ]
                }, columnIndex), column, columnIndex))
        })
    });
};
// Escape cells that contain commas, quotes, or new lines
const escapeCSV = (value)=>{
    if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
};
// Helper function to convert table data to CSV format
export const convertTableDataToCsv = (columns, data)=>{
    const headers = columns.map((col)=>escapeCSV(col.title)).filter(Boolean);
    const rows = data.map((row)=>columns.map((col)=>{
            if (typeof col.valueFormatter === "function") {
                const formattedValue = col.valueFormatter(row, 0);
                if (/*#__PURE__*/ React.isValidElement(formattedValue)) {
                    // Empty col title
                    if (col.title === "") {
                        return "";
                    }
                    return row.app_user_id ?? "";
                }
                return escapeCSV(String(formattedValue));
            }
            return escapeCSV(String(row[col.title] || ""));
        }));
    return [
        headers,
        ...rows
    ].map((row)=>row.join(",")).join("\n");
};
export const exportTableDataToCSV = (csvContent, filename = "export.csv")=>{
    const blob = new Blob([
        csvContent
    ], {
        type: "text/csv;charset=utf-8;"
    });
    const link = document.createElement("a");
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};
